/**
 * Experiment Element Visibility
 * 
 * Manages element visibility based on experiment state and viewport.
 * 
 * Context: Address Page
 * Rules are organized by UI concern in the /rules directory:
 * - carousel-rules.ts - Carousel share/like buttons
 * - hero-rules.ts - Hero section elements (LISTING_HERO experiment)
 * - content-rules.ts - Listing content elements (Open House Card)
 * - footer-rules.ts - Footer CTA elements (Sticky Footer)
 * - sticky-header-rules.ts - Sticky header elements (STICKY_HEADER_TABS experiment)
 */

import { VISIBILITY_RULES } from './rules/address-page';
import { ExperimentElement, ExperimentState, VisibilityContext, Viewport } from './types';

/**
 * Evaluate a visibility rule for an element
 */
function evaluateRule(
  element: ExperimentElement,
  context: VisibilityContext
): boolean {
  const rule = VISIBILITY_RULES[element];
  
  // No rule = always show (default)
  if (!rule) {
    return true;
  }
  
  // If rule is a function, call it directly
  if (typeof rule === 'function') {
    return rule(context);
  }
  
  // Otherwise, evaluate declarative rule
  const { experiments, viewport } = context;
  
  // Check hideWhen first (takes precedence over showWhen)
  if (rule.hideWhen) {
    const { experiments: exp, viewports } = rule.hideWhen;
    
    // If viewport restriction exists, check if current viewport matches
    if (!viewports || viewports.includes(viewport)) {
      // Check if any of the experiments are active
      const shouldHide = exp.some(e => experiments[e]);
      if (shouldHide) {
        return false; // hideWhen takes precedence - hide the element
      }
    }
  }
  
  // showWhen: element ONLY shows when these experiments are active
  if (rule.showWhen) {
    const { experiments: exp, viewports } = rule.showWhen;
    
    // If viewport restriction exists, check if current viewport matches
    if (viewports && !viewports.includes(viewport)) {
      return false; // Wrong viewport for this showWhen rule
    }
    
    // Check if any of the experiments are active
    return exp.some(e => experiments[e]);
  }
  
  // Default: show (only if no showWhen rule exists)
  return true;
}

/**
 * Experiment Element Visibility Manager
 * 
 * Determines visibility of elements based on experiment state and viewport.
 * Can be used in any page context with experiment-driven UI variations.
 */
export class ExperimentElementVisibility {
  private context: VisibilityContext;

  constructor(experimentState: ExperimentState, viewport: Viewport) {
    this.context = { experiments: experimentState, viewport };
  }

  /**
   * Check if an element should be visible
   */
  shouldShow(element: ExperimentElement): boolean {
    return evaluateRule(element, this.context);
  }

  /**
   * Get visibility for multiple elements at once
   */
  getVisibility(elements: ExperimentElement[]): Record<string, boolean> {
    return elements.reduce((acc, element) => {
      acc[element] = this.shouldShow(element);
      return acc;
    }, {} as Record<string, boolean>);
  }
}

/**
 * Create an experiment element visibility manager instance
 */
export function createExperimentElementVisibility(
  experimentState: ExperimentState,
  viewport: Viewport
): ExperimentElementVisibility {
  return new ExperimentElementVisibility(experimentState, viewport);
}
